import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core/react/i18n';

import type { Endorsement } from '../../api';
import { EndorsementCardEdit } from '../EndorsementCardEdit';
import { EndorsementModal } from '../EndorsementModal';

import { DeleteEndorsementModal } from './components/DeleteEndorsementModal';
import {
	actionButtonsContainer,
	actionButtonsContainerEmptyState,
	descriptionContainer,
	endorsementCardContainer,
	sectionContainer,
	titleContainer,
} from './EndorsementSection.styles';
import { useEndorsementSection } from './hooks/useEndorsementSection';

const getEndorsementDescription = (content: string | null | undefined, isContentExpanded: boolean): string => {
	const fullContent = content ?? '';
	const hasLongContent = fullContent.length > 200;
	const shouldTruncate = hasLongContent && !isContentExpanded;

	return shouldTruncate ? `${fullContent.slice(0, 200)}...` : fullContent;
};

const EndorsementItem: React.FC<
	Readonly<{
		endorsement: Endorsement;
		isContentExpanded: boolean;
		onToggleExpansion: () => void;
		onEdit: (id: string) => void;
		onRemove: (id: string) => void;
	}>
> = ({ endorsement, isContentExpanded, onToggleExpansion, onEdit, onRemove }) => {
	const contentLength = endorsement.content?.length ?? 0;
	const shouldShowReadMore = contentLength > 200;
	const displayedDescription = getEndorsementDescription(endorsement.content, isContentExpanded);
	const readMoreButtonLabel = isContentExpanded ? 'View less' : 'Read full endorsement';

	return (
		<EndorsementCardEdit
			key={endorsement.id}
			name={endorsement.name}
			title={endorsement.title ?? ''}
			description={displayedDescription}
			photoUrl={endorsement.photo?.userSmall.url ?? ''}
			link={endorsement.url ?? '#'}
			onEdit={() => onEdit(endorsement.id)}
			onRemove={() => onRemove(endorsement.id)}
			onReadMore={shouldShowReadMore ? onToggleExpansion : undefined}
			readMoreLabel={shouldShowReadMore ? readMoreButtonLabel : undefined}
			className="bg-background-base border-stroke-weak rounded-lg border"
		/>
	);
};

const EndorsementList: React.FC<
	Readonly<{
		endorsements: readonly Endorsement[];
		isContentExpanded: boolean;
		onToggleExpansion: () => void;
		onEdit: (id: string) => void;
		onRemove: (id: string) => void;
	}>
> = ({ endorsements, isContentExpanded, onToggleExpansion, onEdit, onRemove }) => {
	return (
		<div className={endorsementCardContainer()}>
			{endorsements.map((endorsement) => (
				<EndorsementItem
					key={endorsement.id}
					endorsement={endorsement}
					isContentExpanded={isContentExpanded}
					onToggleExpansion={onToggleExpansion}
					onEdit={onEdit}
					onRemove={onRemove}
				/>
			))}
		</div>
	);
};

export const EndorsementSection: React.FC = () => {
	const {
		endorsements,
		isContentExpanded,
		isDeleteModalOpen,
		isDeleting,
		isHelpModalOpen,
		setIsDeleteModalOpen,
		setIsHelpModalOpen,
		handleAddAnother,
		handleViewOnPetition,
		handleEdit,
		handleRemove,
		handleSeeExampleClick,
		handleConfirmDelete,
		handleToggleContentExpansion,
	} = useEndorsementSection();

	const isEmpty = endorsements.length === 0;

	return (
		<div className={sectionContainer()} data-testid="endorsement-section" id="endorsements">
			<div className={titleContainer()}>
				<Text variant="heading" size="x-small" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.title" />
				</Text>
			</div>

			<div className={descriptionContainer()}>
				<Text variant="body" className="text-typography-strong">
					<Translate value="corgi.petition.edit.endorsements.description" />
					<br />
					<button
						type="button"
						className="text-typography-secondary cursor-pointer border-none bg-transparent p-0 underline"
						onClick={handleSeeExampleClick}
					>
						<Translate value="corgi.petition.edit.endorsements.see_example" />
					</button>
				</Text>
			</div>

			{isEmpty ? (
				<div className={actionButtonsContainerEmptyState()}>
					<Button onClick={handleAddAnother} variant="secondary" size="x-small" data-testid="add-endorsement-button">
						<Translate value="corgi.petition.edit.endorsements.add_endorsement_button" />
					</Button>
				</div>
			) : (
				<>
					<EndorsementList
						endorsements={endorsements}
						isContentExpanded={isContentExpanded}
						onToggleExpansion={handleToggleContentExpansion}
						onEdit={handleEdit}
						onRemove={handleRemove}
					/>

					<div className={actionButtonsContainer()}>
						<Button
							onClick={handleAddAnother}
							variant="secondary"
							size="x-small"
							data-testid="add-another-endorsement-button"
						>
							<Translate value="corgi.petition.edit.endorsements.add_another_button" />
						</Button>
						<Button
							onClick={handleViewOnPetition}
							variant="tertiary"
							size="x-small"
							data-testid="view-on-petition-button"
						>
							<Translate value="corgi.petition.edit.endorsements.view_on_petition_button" />
						</Button>
					</div>

					<DeleteEndorsementModal
						isOpen={isDeleteModalOpen}
						onOpenChange={setIsDeleteModalOpen}
						onConfirmDelete={handleConfirmDelete}
						isDeleting={isDeleting}
					/>
				</>
			)}

			<EndorsementModal variant="help" isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
		</div>
	);
};
